// ✅ src/pages/VehicleV2/TyreMaintenanceReport.jsx
// UPDATED — Production listing template
// 3-column filters • overflow locked • important list columns

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaArrowLeft,
  FaFileExport,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
  FaEye,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/tyreMaintenanceReport";

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const exportToCsv = (rows, filename = "tyre-maintenance-report.csv") => {
  if (!rows?.length) return;

  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const cell = r[h] ?? "";
          const s = String(cell).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const pick = (row, keys) => {
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
};

const createDefaultFilters = () => ({
  fromDate: "",
  toDate: "",
  serialId: 0,
  tyreTypeId: 0,
  vehicleId: 0,
  storeId: 0,
});

export default function TyreMaintenanceReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    "0";

  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toNum(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toNum(loginUserId, 0),
    }),
    [sessionBranchId, branchType, loginUserId]
  );

  const defaultFilters = useMemo(() => createDefaultFilters(), []);

  const didInitRef = useRef(false);

  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const isBusy = pageLoading || dropdownLoading || listLoading;

  const [showFilters, setShowFilters] = useState(true);

  const [meta, setMeta] = useState({
    serials: [],
    tyreTypes: [],
    vehicles: [],
    stores: [],
  });

  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const totalRows = rows.length;
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalRows / itemsPerPage)),
    [totalRows]
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = useMemo(
    () => rows.slice(indexOfFirstItem, indexOfLastItem),
    [rows, indexOfFirstItem, indexOfLastItem]
  );

  const fieldClass = useMemo(
    () =>
      `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const tdClass = useMemo(
    () => `px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`,
    [isDark]
  );

  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const loadMeta = useCallback(async () => {
    try {
      setDropdownLoading(true);
      const { data } = await axios.get(`${API_BASE}/meta`, {
        params: { branchId: session.branchId },
        headers: commonHeaders,
      });

      if (!data?.success) throw new Error(data?.message || "Meta load failed");

      setMeta({
        serials: toList(data.serials),
        tyreTypes: toList(data.tyreTypes),
        vehicles: toList(data.vehicles),
        stores: toList(data.stores),
      });
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Failed to load dropdowns");
      setMeta({ serials: [], tyreTypes: [], vehicles: [], stores: [] });
    } finally {
      setDropdownLoading(false);
    }
  }, [session.branchId, commonHeaders]);

  const loadList = useCallback(
    async (nextFilters = filters) => {
      try {
        setListLoading(true);
        const params = {
          branchId: session.branchId,
          fromDate: toStr(nextFilters.fromDate),
          toDate: toStr(nextFilters.toDate),
          serialId: toNum(nextFilters.serialId, 0),
          tyreTypeId: toNum(nextFilters.tyreTypeId, 0),
          vehicleId: toNum(nextFilters.vehicleId, 0),
          storeId: toNum(nextFilters.storeId, 0),
        };

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) throw new Error(data?.message || "List load failed");

        setRows(toList(data.rows));
        setCurrentPage(1);
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Failed to load report");
        setRows([]);
        setCurrentPage(1);
      } finally {
        setListLoading(false);
      }
    },
    [filters, session.branchId, commonHeaders]
  );

  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);
      setFilters(defaultFilters);
      setRows([]);
      setCurrentPage(1);
      await loadMeta();
    } finally {
      setPageLoading(false);
    }
  }, [defaultFilters, loadMeta]);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      if (["serialId", "tyreTypeId", "vehicleId", "storeId"].includes(name)) {
        setField(name, toNum(value, 0));
        return;
      }
      setField(name, value);
    },
    [setField]
  );

  const handleSearch = useCallback(async () => {
    await loadList(filters);
    toast.success("Report loaded");
  }, [loadList, filters]);

  const handleViewAll = useCallback(async () => {
    const reset = createDefaultFilters();
    setFilters(reset);
    await loadList(reset);
    toast.info("Showing all records");
  }, [loadList]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    await loadList(defaultFilters);
    toast.info("Page refreshed");
  }, [loadPage, loadList, defaultFilters]);

  const handleReset = useCallback(() => {
    setFilters(createDefaultFilters());
    setRows([]);
    setCurrentPage(1);
    toast.info("Filters reset. Click Search to load.");
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleAllPrint = useCallback(() => {
    navigate("/vehicle/TyreMaintenanceReport_Print", {
      state: {
        filters: {
          branchId: session.branchId,
          fromDate: toStr(filters.fromDate),
          toDate: toStr(filters.toDate),
          serialId: toNum(filters.serialId, 0),
          tyreTypeId: toNum(filters.tyreTypeId, 0),
          vehicleId: toNum(filters.vehicleId, 0),
          storeId: toNum(filters.storeId, 0),
        },
      },
    });
    toast.info("Printing all tyre maintenance records");
  }, [navigate, session.branchId, filters]);

  const handlePrintRow = useCallback(
    (row) => {
      const rowId = toNum(pick(row, ["fld_AddTyre_ID", "id", "AddTyreID"]), 0);
      if (!rowId) {
        toast.error("Cannot print: Row ID not found");
        return;
      }

      navigate("/vehicle/TyreMaintenanceReport_Print", {
        state: {
          filters: {
            branchId: session.branchId,
            rowId,
            fromDate: toStr(filters.fromDate),
            toDate: toStr(filters.toDate),
            serialId: toNum(filters.serialId, 0),
            tyreTypeId: toNum(filters.tyreTypeId, 0),
            vehicleId: toNum(filters.vehicleId, 0),
            storeId: toNum(filters.storeId, 0),
          },
        },
      });
    },
    [navigate, session.branchId, filters]
  );

  const handleExport = useCallback(() => {
    try {
      if (!rows.length) {
        toast.info("No rows to export");
        return;
      }
      exportToCsv(rows);
      toast.success("Exported CSV");
    } catch (e) {
      console.error(e);
      toast.error("Export failed");
    }
  }, [rows]);

  const goPage = useCallback(
    (p) => {
      setCurrentPage(Math.min(Math.max(1, p), totalPages));
    },
    [totalPages]
  );

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => {
      await loadPage();
      await loadList(defaultFilters);
    })();
  }, [loadPage, loadList, defaultFilters]);

  const statusText = pageLoading
    ? "Loading page..."
    : dropdownLoading
    ? "Loading dropdowns..."
    : listLoading
    ? "Searching..."
    : "Ready";

  return (
    <VehicleLayoutWrapper>
      <div
        className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-2xl md:text-3xl">
              TYRE MAINTENANCE REPORT
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>
                Tyre Maintenance Report
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} • User ID: {session.loginUserId || "-"}
              </p>
            </div>

            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button
                onClick={handleAllPrint}
                disabled={isBusy || rows.length === 0}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                  isBusy || rows.length === 0 ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <FaPrint /> Print
              </button>
              <button
                onClick={handleExport}
                disabled={isBusy || rows.length === 0}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy || rows.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFileExport /> Export
              </button>
              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt /> Refresh
              </button>
              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft /> Back
              </button>
              <button
                onClick={() => setShowFilters((p) => !p)}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={handleReset}
                    type="button"
                    disabled={isBusy}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleViewAll}
                    type="button"
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <FaEye /> View All
                  </button>
                  <button
                    onClick={handleSearch}
                    type="button"
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch /> {listLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className={labelClass}>From Date</label>
                  <input type="date" name="fromDate" value={filters.fromDate} onChange={handleChange} className={fieldClass} disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>To Date</label>
                  <input type="date" name="toDate" value={filters.toDate} onChange={handleChange} className={fieldClass} disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Tyre Serial Number</label>
                  <select name="serialId" value={filters.serialId} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value={0}>Select</option>
                    {meta.serials.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Number</label>
                  <select name="vehicleId" value={filters.vehicleId} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value={0}>Select</option>
                    {meta.vehicles.map((v) => (
                      <option key={v.value} value={v.value}>{v.label}</option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Store</label>
                  <select name="storeId" value={filters.storeId} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value={0}>Select</option>
                    {meta.stores.map((st) => (
                      <option key={st.value} value={st.value}>{st.label}</option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Tyre Type</label>
                  <select name="tyreTypeId" value={filters.tyreTypeId} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value={0}>Select</option>
                    {meta.tyreTypes.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
              Total Records: {totalRows}
            </div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{statusText}</div>
          </div>

          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || dropdownLoading || listLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div>
              </div>
            ) : currentItems.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records Found</div>
                <p className={`mt-2 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>Try adjusting your search criteria or click View All.</p>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">S.No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Vehicle No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Tyre Serial No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Tyre Type</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Tyre Make</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Tyre Position</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Print</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {currentItems.map((r, idx) => {
                    const vehicleNo = pick(r, ["fld_vehicle_regno", "VehicleNo"]);
                    const tyreSerial = pick(r, ["fld_Tyreserialno", "fld_vehicle_serialno"]);
                    const tyreType = pick(r, ["fld_tyretypename", "fld_tyretype"]);
                    const tyreMake = pick(r, ["fld_TyreMake", "fld_tyremake"]);
                    const tyrePosition = pick(r, ["fld_vehicletyre_positiontype"]);
                    const amount = pick(r, ["fld_TyreAmounts", "fld_tyreamt"]);

                    return (
                      <tr
                        key={`${pick(r, ["fld_AddTyre_ID", "id", "AddTyreID"]) || idx}-${idx}`}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={tdClass}>{indexOfFirstItem + idx + 1}</td>
                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{vehicleNo || "-"}</td>
                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>{tyreSerial || "-"}</td>
                        <td className={tdClass}>{tyreType || "-"}</td>
                        <td className={tdClass}>{tyreMake || "-"}</td>
                        <td className={tdClass}>{tyrePosition || "-"}</td>
                        <td className={`px-4 py-3 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>
                          {amount
                            ? `₹${parseFloat(amount).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`
                            : "-"}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handlePrintRow(r)}
                            disabled={isBusy}
                            className={`p-2 rounded transition ${
                              isDark ? "text-white hover:bg-gray-600" : "text-gray-800 hover:bg-gray-200"
                            } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                            title="Print"
                            type="button"
                          >
                            <FaPrint className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {totalRows > 0 && (
            <div className="mt-6 flex justify-center items-center gap-3 flex-wrap">
              <button
                className={`px-4 py-2 border rounded-lg font-semibold ${
                  isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"
                } ${isBusy || currentPage <= 1 ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={() => goPage(currentPage - 1)}
                disabled={isBusy || currentPage <= 1}
                type="button"
              >
                Prev
              </button>
              <div className={`px-4 py-2 border rounded-lg ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}>
                Page {currentPage} / {totalPages}
              </div>
              <button
                className={`px-4 py-2 border rounded-lg font-semibold ${
                  isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"
                } ${isBusy || currentPage >= totalPages ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={() => goPage(currentPage + 1)}
                disabled={isBusy || currentPage >= totalPages}
                type="button"
              >
                Next
              </button>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}